import { RiCloseLine, RiDeleteBinFill, RiEdit2Line } from "react-icons/ri";
import Typography from "../../Utility/Typography";
import Checkbox from "../../Utility/Checkbox/Checkbox";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import Button from "../../Utility/Button/Button";
import { FormSubmitRoundSpinner } from "../../Utility/Button/FormSubmitButton";
import FloatingTooltip from "../../Utility/FloatingTooltip/FloatingTooltip";
import { formatDate } from "../../utils/dateUtils";
import {
  getAssigneeDisplay,
  getIssueListPriorityBorderStyle,
  getIssueListPriorityBadgeStyle,
  getIssueListStatusBadgeStyle,
  getIssueListPriorityIcon,
  getIssueListStatusIcon,
  resolveWorkItemTypeDisplayName,
  getWorkItemParentDisplayName,
  resolveProjectStatusOptionValue,
  formatWorkItemProjectName,
} from "./issueMappers";
import WorkItemTypeIcon from "./WorkItemTypeIcon";

export default function IssueWorkItemRow({
  issue,
  workItemTypeOptions = null,
  projectSlug,
  currentProjectTab,
  navigate,
  onOpenWorkItemDetail,
  selected,
  onSelectIssue,
  onRowNavigate,
  editingField,
  setEditingField,
  onDoubleClick,
  onInlineEdit,
  priorityOptionsForDropdown,
  statusOptions,
  assigneeOptions,
  allowStatusUpdate,
  getStatusText,
  onDeleteClick,
  canEditWorkItems = true,
  canDeleteWorkItems = true,
  showActionsColumn = true,
  prioritySaving = false,
  assigneeSaving = false,
  constrainWorkColumn = false,
}) {
  const isEditingPriority =
    editingField?.issueId === issue.id && editingField?.field === "priority";
  const isEditingAssignee =
    editingField?.issueId === issue.id && editingField?.field === "assignee";
  const allowStatusEdit = allowStatusUpdate && canEditWorkItems;
  const isEditingStatus =
    allowStatusEdit && editingField?.issueId === issue.id && editingField?.field === "status";

  const workItemTypeLabel = resolveWorkItemTypeDisplayName(issue, workItemTypeOptions ?? []);
  const parentDisplayName = getWorkItemParentDisplayName(issue);
  const workTitle = issue.title || "N/A";
  const issueKeyLabel = issue.issueKey != null ? String(issue.issueKey) : "";

  const openWorkItemFromKey = (e) => {
    e.stopPropagation();
    const projectIdForUrl = projectSlug ?? issue.projectId ?? issue.project?.id;
    if (projectIdForUrl && onOpenWorkItemDetail) {
      onOpenWorkItemDetail(issue.id, projectIdForUrl);
    } else if (projectIdForUrl) {
      navigate(
        `/projects/${projectIdForUrl}?tab=${encodeURIComponent(currentProjectTab || "issues")}`
      );
    } else {
      navigate(`/create-issue?id=${issue.id}`);
    }
  };

  const wrapWorkLineTooltip = (tooltip, line) =>
    constrainWorkColumn && tooltip ? (
      <FloatingTooltip label={tooltip} anchorClassName="inline-block max-w-full min-w-0">
        {line}
      </FloatingTooltip>
    ) : (
      line
    );

  const issueKeyLine = (
    <Typography
      variant="span1"
      className="text-xs font-medium text-gray-500 cursor-pointer underline hover:opacity-80 text-text-link "
      onClick={openWorkItemFromKey}
    >
      {issueKeyLabel}
    </Typography>
  );

  const titleLine = (
    <Typography variant="span1" className="text-sm font-medium text-gray-900">
      {workTitle}
    </Typography>
  );

  return (
    <tr
      className="hover:bg-gray-50 transition-colors border-l-4 cursor-pointer"
      style={getIssueListPriorityBorderStyle(issue.priority)}
      onClick={onRowNavigate}
    >
      <td className="text-start align-middle px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          name={`issue-${issue.id}`}
          checked={selected}
          onChange={(e) => onSelectIssue(issue.id, e.target.checked)}
          noWrapper={true}
        />
      </td>

      <td className="text-start align-middle px-3 py-3">
        <Typography variant="span1" className="text-sm text-gray-700">
          {issue.clientName?.trim() ? issue.clientName : "N/A"}
        </Typography>
      </td>

      <td className="text-start align-middle px-3 py-3">
        <Typography variant="span1" className="text-sm text-gray-700">
          {formatWorkItemProjectName(issue) || "N/A"}
        </Typography>
      </td>

      <td
        className={`text-start align-middle px-3 py-3${constrainWorkColumn ? " work-items-work-col" : ""}`}
      >
        <div
          className={`flex flex-col gap-1${constrainWorkColumn ? " work-items-work-col-inner min-w-0" : ""}`}
        >
          {issueKeyLine}
          {wrapWorkLineTooltip(workTitle, titleLine)}
        </div>
      </td>

      <td className="text-start align-middle px-3 py-3">
        <Typography variant="span1" className="text-sm text-gray-700">
          {parentDisplayName || "N/A"}
        </Typography>
      </td>

      <td className="text-start align-middle px-3 py-3">
        <Typography
          variant="span1"
          className="text-sm text-text-primary inline-flex items-center gap-2"
        >
          <WorkItemTypeIcon typeLabel={workItemTypeLabel} sizeClassName="text-lg" />
          <span>{workItemTypeLabel || "N/A"}</span>
        </Typography>
      </td>

      <td
        className="text-start align-middle px-3 py-3"
        onDoubleClick={
          canEditWorkItems
            ? (e) => {
                e.stopPropagation();
                onDoubleClick(issue.id, "priority");
              }
            : undefined
        }
      >
        {isEditingPriority ? (
          prioritySaving ? (
            <div
              className="flex items-center gap-2 min-h-[2.25rem]"
              role="status"
              aria-live="polite"
              aria-label="Updating priority"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="inline-flex items-center gap-2">
                <FormSubmitRoundSpinner />
                <Typography variant="text2" component="span" className="text-gray-700">
                  Saving…
                </Typography>
                <Typography variant="text2" className="sr-only" component="span">
                  Loading...
                </Typography>
              </span>
            </div>
          ) : (
            <div className="dropdown-wrapper flex items-center gap-2">
              <Dropdown
                name="priority"
                value={issue.priorityId ?? issue.priority ?? ""}
                onChange={(e) => {
                  if (e.target.value) {
                    onInlineEdit(issue.id, "priority", e.target.value);
                  }
                }}
                options={priorityOptionsForDropdown}
                placeholder="Select priority"
                noWrapper={true}
                className=""
              />
              <Button
                type="button"
                variant="icon"
                size="vsm"
                aria-label="Cancel"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingField(null);
                }}
                className="p-1 rounded hover:bg-gray-200 text-gray-600 hover:text-gray-800"
              >
                <RiCloseLine className="text-sm" />
              </Button>
            </div>
          )
        ) : (
          <div
            className="group inline-flex items-center gap-1.5"
            title={canEditWorkItems ? "Double-click to edit priority" : undefined}
          >
            <Typography
              variant="span1"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border transition-all duration-200 ease-in-out ${
                canEditWorkItems
                  ? "cursor-pointer group-hover:ring-2 group-hover:ring-blue-300 group-hover:ring-offset-1"
                  : ""
              }`}
              style={getIssueListPriorityBadgeStyle(issue.priority)}
            >
              {getIssueListPriorityIcon(issue.priority)}
              {issue.priority || "N/A"}
            </Typography>
            {canEditWorkItems ? (
              <RiEdit2Line className="text-xs text-gray-400 opacity-40 group-hover:opacity-100 group-hover:text-blue-500 transition-opacity duration-200 ease-in-out cursor-pointer" />
            ) : null}
          </div>
        )}
      </td>

      <td
        className="text-start align-middle px-3 py-3"
        onDoubleClick={
          canEditWorkItems
            ? (e) => {
                e.stopPropagation();
                onDoubleClick(issue.id, "assignee");
              }
            : undefined
        }
      >
        {isEditingAssignee ? (
          assigneeSaving ? (
            <div
              className="flex items-center gap-2 min-h-[2.25rem]"
              role="status"
              aria-live="polite"
              aria-label="Updating assignee"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="inline-flex items-center gap-2">
                <FormSubmitRoundSpinner />
                <Typography variant="text2" component="span" className="text-gray-700">
                  Saving…
                </Typography>
                <Typography variant="text2" className="sr-only" component="span">
                  Loading...
                </Typography>
              </span>
            </div>
          ) : (
            <div className="dropdown-wrapper flex items-center gap-2">
              <Dropdown
                name="assignee"
                value={issue.assigneeId != null ? String(issue.assigneeId) : ""}
                onChange={(e) => {
                  if (e.target.value) {
                    onInlineEdit(issue.id, "assignee", e.target.value);
                  }
                }}
                options={assigneeOptions}
                placeholder="Select assignee"
                noWrapper={true}
                className="w-full"
                searchable={true}
              />
              <Button
                type="button"
                variant="icon"
                size="vsm"
                aria-label="Cancel"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingField(null);
                }}
                className="p-1 rounded hover:bg-gray-200 text-gray-600 hover:text-gray-800"
              >
                <RiCloseLine className="text-sm" />
              </Button>
            </div>
          )
        ) : (
          <div
            className="group inline-flex items-center gap-1.5"
            title={canEditWorkItems ? "Double-click to edit assignee" : undefined}
          >
            <Typography
              variant="span1"
              className={`text-sm text-gray-700 px-2 py-1 rounded ${
                canEditWorkItems
                  ? "cursor-pointer group-hover:ring-2 group-hover:ring-blue-300 group-hover:ring-offset-1"
                  : ""
              }`}
            >
              {getAssigneeDisplay(issue.assignee) ?? (
                <Typography variant="span1" className="text-gray-400" component="span">
                  Unassigned
                </Typography>
              )}
            </Typography>
            {canEditWorkItems ? (
              <RiEdit2Line className="text-xs text-gray-400 opacity-40 group-hover:opacity-100 group-hover:text-blue-500 transition-opacity duration-200 ease-in-out cursor-pointer" />
            ) : null}
          </div>
        )}
      </td>

      <td className="text-start align-middle px-3 py-3">
        <Typography variant="span1" className="text-sm text-gray-700">
          {issue.reporter || "N/A"}
        </Typography>
      </td>

      <td
        className="text-start align-middle px-3 py-3"
        onDoubleClick={
          allowStatusEdit
            ? (e) => {
                e.stopPropagation();
                onDoubleClick(issue.id, "status");
              }
            : undefined
        }
      >
        {isEditingStatus ? (
          <div className="dropdown-wrapper flex items-center gap-2">
            <Dropdown
              name="status"
              value={
                resolveProjectStatusOptionValue(issue, null, statusOptions) ||
                (Array.isArray(statusOptions)
                  ? statusOptions.find(
                      (o) =>
                        typeof o === "object" && String(o.value) === String(issue.statusId ?? "")
                    )?.value
                  : undefined) ||
                ""
              }
              onChange={(e) => {
                if (e.target.value) {
                  onInlineEdit(issue.id, "status", e.target.value);
                }
              }}
              options={statusOptions}
              placeholder="Select status"
              noWrapper={true}
              className="w-1/2"
            />
            <Button
              type="button"
              variant="icon"
              size="vsm"
              aria-label="Cancel"
              onClick={(e) => {
                e.stopPropagation();
                setEditingField(null);
              }}
              className="p-1 rounded hover:bg-gray-200 text-gray-600 hover:text-gray-800"
            >
              <RiCloseLine className="text-sm" />
            </Button>
          </div>
        ) : (
          <div
            className="group inline-flex items-center gap-1.5"
            title={allowStatusEdit ? "Double-click to edit status" : undefined}
          >
            <Typography
              variant="span1"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border transition-all duration-200 ease-in-out ${
                allowStatusEdit
                  ? "cursor-pointer group-hover:ring-2 group-hover:ring-blue-300 group-hover:ring-offset-1"
                  : ""
              }`}
              style={getIssueListStatusBadgeStyle(issue)}
            >
              {getIssueListStatusIcon(issue)}
              {getStatusText(issue.status)}
            </Typography>
            {allowStatusEdit ? (
              <RiEdit2Line className="text-xs text-gray-400 opacity-40 group-hover:opacity-100 group-hover:text-blue-500 transition-opacity duration-200 ease-in-out cursor-pointer" />
            ) : null}
          </div>
        )}
      </td>

      <td className="text-start align-middle px-3 py-3">
        <Typography variant="span1" className="text-sm text-gray-700">
          {formatDate(issue.createdAt)}
        </Typography>
      </td>
      <td className="text-start align-middle px-3 py-3">
        <Typography variant="span1" className="text-sm text-gray-700">
          {formatDate(issue.updatedAt)}
        </Typography>
      </td>
      <td className="text-start align-middle px-3 py-3">
        <Typography variant="span1" className="text-sm text-gray-700">
          {formatDate(issue.dueDate)}
        </Typography>
      </td>
      {showActionsColumn ? (
        <td className="text-start align-middle px-3 py-3" onClick={(e) => e.stopPropagation()}>
          {canDeleteWorkItems ? (
            <FloatingTooltip label="Delete">
              <Button
                onClick={(e) => onDeleteClick(issue, e)}
                aria-label="Delete work item"
                size="sm"
                className="btn-danger-new !w-7 !h-7 !p-0 flex items-center justify-center hover:opacity-90 border-0"
              >
                <RiDeleteBinFill className="text-white text-[12px]" />
              </Button>
            </FloatingTooltip>
          ) : null}
        </td>
      ) : null}
    </tr>
  );
}
